import * as api from '../api';
import { PURGE } from 'redux-persist';

const GET_ADMINS = 'admins/GET_ADMINS';
const GET_ADMIN_ACCOUNT_TYPES = 'admins/GET_ADMIN_ACCOUNT_TYPES';
const POST_ADMIN = 'admins/POST_ADMIN';
const UPDATE_ADMIN = 'admins/UPDATE_ADMIN';
const TOGGLE_WAIVER_ALERTS = 'admins/TOGGLE_WAIVER_ALERTS';
const TOGGLE_RESERVATION_ALERTS = 'admins/TOGGLE_RESERVATION_ALERTS';
const TOGGLE_VEHICLE_ALERTS = 'admins/TOGGLE_VEHICLE_ALERTS';
const initialState = { admins: [], account_types: [], new_admin: null };

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case GET_ADMINS:
      return Object.assign({}, state, { admins: action.data, new_admin: null });
    case GET_ADMIN_ACCOUNT_TYPES:
      return Object.assign({}, state, { account_types: action.data });
    case POST_ADMIN:
      return Object.assign({}, state, { new_admin: action.data });
    case UPDATE_ADMIN: {
      const updatedAdmins = state.admins.map((admin) =>
        admin.id === action.data.id ? action.data : admin
      );
      return Object.assign({}, state, { admins: updatedAdmins });
    }
    case TOGGLE_WAIVER_ALERTS: {
        // Check if we're receiving the new format (object with userId) or old format (just userId)
        const isNewFormat = typeof action.data === 'object' && action.data !== null;
        
        // Extract the userId and targetState from the action data
        const userId = isNewFormat ? action.data.userId : action.data;
        const targetState = isNewFormat ? action.data.targetState : undefined;
        
        // Create a new admins array with updated waiver_alerts_enabled
        const updatedAdmins = state.admins.map((admin) => {
          if (admin.id === userId) {
            // Create a deep copy of the admin to avoid mutating state
            const updatedAdmin = { 
              ...admin,
              fleets: admin.fleets.map(fleet => ({
                ...fleet,
                // If targetState is provided, use it directly; otherwise toggle the current value
                waiver_alerts_enabled: targetState !== undefined 
                  ? targetState 
                  : !fleet.waiver_alerts_enabled
              }))
            };
            return updatedAdmin;
          }
          return admin;
        });
        
        return {
          ...state,
          admins: updatedAdmins
        };
      }
    case TOGGLE_RESERVATION_ALERTS: {
        // Check if we're receiving the new format (object with userId) or old format (just userId)
        const isNewFormat = typeof action.data === 'object' && action.data !== null;
        
        // Extract the userId and targetState from the action data
        const userId = isNewFormat ? action.data.userId : action.data;
        const targetState = isNewFormat ? action.data.targetState : undefined;
        
        // Create a new admins array with updated reservation_alerts_enabled
        const updatedAdmins = state.admins.map((admin) => {
          if (admin.id === userId) {
            // Create a deep copy of the admin to avoid mutating state
            const updatedAdmin = { 
              ...admin,
              fleets: admin.fleets.map(fleet => ({
                ...fleet,
                // If targetState is provided, use it directly; otherwise toggle the current value
                reservation_alerts_enabled: targetState !== undefined 
                  ? targetState 
                  : !fleet.reservation_alerts_enabled
              }))
            };
            return updatedAdmin;
          }
          return admin;
        });
        
        return {
          ...state,
          admins: updatedAdmins
        };
      }
    case TOGGLE_VEHICLE_ALERTS: {
        // Check if we're receiving the new format (object with userId) or old format (just userId)
        const isNewFormat = typeof action.data === 'object' && action.data !== null;
        
        // Extract the userId and targetState from the action data
        const userId = isNewFormat ? action.data.userId : action.data;
        const targetState = isNewFormat ? action.data.targetState : undefined;
        
        // Create a new admins array with updated vehicle_alerts_enabled
        const updatedAdmins = state.admins.map((admin) => {
          if (admin.id === userId) {
            // Create a deep copy of the admin to avoid mutating state
            const updatedAdmin = { 
              ...admin,
              fleets: admin.fleets.map(fleet => ({
                ...fleet,
                // If targetState is provided, use it directly; otherwise toggle the current value
                vehicle_alerts_enabled: targetState !== undefined 
                  ? targetState 
                  : !fleet.vehicle_alerts_enabled
              }))
            };
            return updatedAdmin;
          }
          return admin;
        });
        
        return {
          ...state,
          admins: updatedAdmins
        };
      }
    case PURGE:
      return initialState;
    default:
      return state;
  }
}

export const getAdminAccountTypes = () => {
  return (dispatch) => {
    return api
      .getAdminAccountTypes()
      .then(({ account_types }) =>
        dispatch({ type: GET_ADMIN_ACCOUNT_TYPES, data: account_types })
      );
  };
};

export const getAdmins = (jwt, fleetId) => {
  return (dispatch) => {
    return api
      .getAdmins(jwt, fleetId)
      .then(({ admins }) => dispatch({ type: GET_ADMINS, data: admins }));
  };
};

export const postAdmin = (jwt, fleetId, newAdmin) => {
  return (dispatch) => {
    return api
      .postAdmin(jwt, fleetId, newAdmin)
      .then(({ admin }) => dispatch({ type: POST_ADMIN, data: admin }));
  };
};

export const updateAdmin = (jwt, fleetId, adminId, updatedAdmin) => {
  return (dispatch) => {
    return api
      .updateAdmin(jwt, fleetId, adminId, updatedAdmin)
      .then(({ admin }) => dispatch({ type: UPDATE_ADMIN, data: admin }));
  };
}

export const deleteAdmin = (jwt, fleetId, adminId) => {
  return (dispatch) => {
    return api.deleteAdmin(jwt, fleetId, adminId);
  };
};

export const toggleWaiverAlerts = (jwt, fleetId, userId, isActive) => {
  return (dispatch) => {
    // Create a unique transaction ID for this toggle operation
    const transactionId = `waiver_${userId}_${Date.now()}`;
    
    // Instead of just passing the userId, pass an object with more detailed information
    dispatch({ 
      type: TOGGLE_WAIVER_ALERTS, 
      data: { 
        userId, 
        targetState: isActive,  // The desired end state
        transactionId 
      } 
    });
    
    // Make the API call
    return api.toggleWaiverAlerts(jwt, fleetId, userId, isActive)
      .then(response => {
        // On success, we could update with the server state if needed
        // For now, we're relying on the optimistic update
        return response;
      })
      .catch((error) => {
        console.error('Error toggling waiver alerts:', error);
        // If it failed, roll back the optimistic change with the same transaction ID
        dispatch({ 
          type: TOGGLE_WAIVER_ALERTS, 
          data: { 
            userId, 
            targetState: !isActive,  // Revert to opposite of what we tried to set
            transactionId,
            isRollback: true 
          } 
        });
        throw error; // Re-throw so the component can handle it
      });
  };
};

export const changeAdminAccountType = (jwt, fleetId, adminId, accountType) => {
  return (dispatch) => {
    return api.changeAdminAccountType(jwt, fleetId, adminId, accountType);
  };
};

export const toggleReservationAlerts = (jwt, fleetId, userId, isEnabled) => {
  return (dispatch) => {
    // Create a unique transaction ID for this toggle operation
    const transactionId = `reservation_${userId}_${Date.now()}`;
    
    // Instead of just passing the userId, pass an object with more detailed information
    dispatch({ 
      type: TOGGLE_RESERVATION_ALERTS, 
      data: { 
        userId, 
        targetState: isEnabled,  // The desired end state
        transactionId 
      } 
    });
    
    // Make the API call
    return api.toggleReservationAlerts(jwt, fleetId, userId, isEnabled)
      .then(response => {
        // On success, we could update with the server state if needed
        // For now, we're relying on the optimistic update
        return response;
      })
      .catch((error) => {
        console.error('Error toggling reservation alerts:', error);
        // If it failed, roll back the optimistic change with the same transaction ID
        dispatch({ 
          type: TOGGLE_RESERVATION_ALERTS, 
          data: { 
            userId, 
            targetState: !isEnabled,  // Revert to opposite of what we tried to set
            transactionId,
            isRollback: true 
          } 
        });
        throw error; // Re-throw so the component can handle it
      });
  };
};

export const toggleVehicleAlerts = (jwt, fleetId, userId, isEnabled) => {
  return (dispatch) => {
    // Create a unique transaction ID for this toggle operation
    const transactionId = `vehicle_${userId}_${Date.now()}`;
    
    // Instead of just passing the userId, pass an object with more detailed information
    dispatch({ 
      type: TOGGLE_VEHICLE_ALERTS, 
      data: { 
        userId, 
        targetState: isEnabled,  // The desired end state
        transactionId 
      } 
    });
    
    // Make the API call
    return api.toggleVehicleAlerts(jwt, fleetId, userId, isEnabled)
      .then(response => {
        // On success, we could update with the server state if needed
        // For now, we're relying on the optimistic update
        return response;
      })
      .catch((error) => {
        console.error('Error toggling vehicle alerts:', error);
        // If it failed, roll back the optimistic change with the same transaction ID
        dispatch({ 
          type: TOGGLE_VEHICLE_ALERTS, 
          data: { 
            userId, 
            targetState: !isEnabled,  // Revert to opposite of what we tried to set
            transactionId,
            isRollback: true 
          } 
        });
        throw error; // Re-throw so the component can handle it
      });
  };
};